import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';
import { Quote, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const TestimonialsSection = () => {
  const { language } = useLanguage();
  const [current, setCurrent] = useState(0);

  const testimonials = language === 'fr' ? [
    {
      quote: "Gaius nous a aidés à comprendre comment intégrer les outils d'IA de manière sécurisée et efficace dans notre pratique. Formation exceptionnelle.",
      author: "Marie D.",
      role: "Associée, Cabinet d'avocats — Paris",
      stars: 5,
    },
    {
      quote: "Après la formation, notre temps de rédaction de notes a diminué de 40%. Le ROI est évident dès la première semaine.",
      author: "Philippe R.",
      role: "Directeur Juridique — Lyon",
      stars: 5,
    },
    {
      quote: "Enfin une formation IA conçue par et pour des professionnels du droit. La double expertise tech et juridique fait toute la différence.",
      author: "Sophie M.",
      role: "Avocate — Bruxelles",
      stars: 5,
    },
    {
      quote: "L'approche agnostique de Gaius nous a permis de choisir les bons outils pour nos besoins spécifiques, sans être liés à un éditeur.",
      author: "Thomas L.",
      role: "Managing Partner — Bordeaux",
      stars: 5,
    },
    {
      quote: "La formation sur le prompt engineering juridique a été un vrai game-changer pour notre équipe corporate. Très concret, très applicable.",
      author: "Claire B.",
      role: "Counsel, Cabinet international — Paris",
      stars: 5,
    },
    {
      quote: "Nous avons formé 25 collaborateurs en trois sessions. L'accompagnement post-formation et le suivi sont remarquables.",
      author: "Jean-Marc P.",
      role: "Secrétaire Général — Groupe CAC 40",
      stars: 5,
    },
    {
      quote: "En tant qu'avocat indépendant, je pensais que l'IA n'était pas pour moi. Gaius m'a prouvé le contraire : j'ai gagné un temps considérable sur mes recherches et ma rédaction.",
      author: "Frédéric V.",
      role: "Avocat indépendant — Toulouse",
      stars: 5,
    },
    {
      quote: "La formation m'a donné une longueur d'avance sur mes confrères. Je maîtrise désormais des techniques de recherche et d'analyse que peu d'avocats connaissent.",
      author: "Léa K.",
      role: "Associate, Cabinet d'affaires — Paris",
      stars: 5,
    },
    {
      quote: "En tant que senior associate, j'encadre des juniors : la formation Gaius m'a permis de structurer l'usage de l'IA dans toute mon équipe, avec des process fiables.",
      author: "Antoine G.",
      role: "Senior Associate, Cabinet international — Paris",
      stars: 5,
    },
    {
      quote: "L'IA appliquée au notariat, c'était flou pour nous. Gaius a su adapter sa formation à nos actes, notre vocabulaire et nos contraintes déontologiques.",
      author: "Maître Hélène C.",
      role: "Notaire — Nantes",
      stars: 5,
    },
    {
      quote: "Je gère seul mon cabinet et chaque heure compte. Grâce à Gaius, j'ai automatisé des tâches répétitives tout en gardant le contrôle sur la qualité de mon travail.",
      author: "Karim S.",
      role: "Avocat indépendant — Marseille",
      stars: 5,
    },
  ] : [
    {
      quote: "Gaius helped our firm understand how to integrate AI tools safely and effectively into our practice. Exceptional training.",
      author: "Marie D.",
      role: "Partner, Law Firm — Paris",
      stars: 5,
    },
    {
      quote: "After the training, our memo drafting time dropped by 40%. The ROI was clear from week one.",
      author: "James S.",
      role: "General Counsel — London",
      stars: 5,
    },
    {
      quote: "Finally, AI training designed by and for legal professionals. The dual tech and legal expertise makes all the difference.",
      author: "Elena R.",
      role: "Barrister — Dublin",
      stars: 5,
    },
    {
      quote: "Gaius's vendor-agnostic approach helped us pick the right tools for our specific needs, without being locked in.",
      author: "Thomas L.",
      role: "Managing Partner — Bordeaux",
      stars: 5,
    },
    {
      quote: "The legal prompt engineering module was a real game-changer for our corporate team. Very practical, immediately applicable.",
      author: "Claire B.",
      role: "Counsel, International Firm — Paris",
      stars: 5,
    },
    {
      quote: "We trained 25 team members in three sessions. The post-training support and follow-up are outstanding.",
      author: "Jean-Marc P.",
      role: "General Secretary — Fortune 500 Company",
      stars: 5,
    },
    {
      quote: "As a solo practitioner, I thought AI wasn't for me. Gaius proved me wrong — I've saved considerable time on research and drafting.",
      author: "Frédéric V.",
      role: "Solo Practitioner — Toulouse",
      stars: 5,
    },
    {
      quote: "The training gave me an edge over my peers. I now master research and analysis techniques that few lawyers know.",
      author: "Léa K.",
      role: "Associate, Business Law Firm — Paris",
      stars: 5,
    },
    {
      quote: "As a senior associate supervising juniors, Gaius helped me structure AI usage across my entire team with reliable processes.",
      author: "Antoine G.",
      role: "Senior Associate, International Firm — Paris",
      stars: 5,
    },
    {
      quote: "AI applied to notarial practice was unclear to us. Gaius tailored the training to our deeds, terminology and ethical constraints.",
      author: "Maître Hélène C.",
      role: "Notary — Nantes",
      stars: 5,
    },
    {
      quote: "I run my practice solo and every hour counts. Thanks to Gaius, I've automated repetitive tasks while keeping full control over quality.",
      author: "Karim S.",
      role: "Solo Practitioner — Marseille",
      stars: 5,
    },
  ];

  const itemsPerView = 3;
  const maxIndex = Math.max(0, testimonials.length - itemsPerView);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent(prev => (prev >= maxIndex ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [maxIndex]);

  const prev = () => setCurrent(c => Math.max(0, c - 1));
  const next = () => setCurrent(c => Math.min(maxIndex, c + 1));

  return (
    <section className="py-24 section-gradient-blue">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            {language === 'fr' ? 'Ce que disent nos clients' : 'What Our Clients Say'}
          </h2>
          <p className="text-xl text-muted-foreground">
            {language === 'fr'
              ? 'Des professionnels du droit de toute l\'Europe nous font confiance'
              : 'Trusted by legal professionals across Europe'}
          </p>
        </div>

        <div className="relative">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${current * (100 / itemsPerView)}%)` }}
            >
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="w-full md:w-1/3 flex-shrink-0 px-3"
                >
                  <Card className="glass-card hover:shadow-xl transition-all duration-300 h-full border-t-2 border-t-primary/30">
                    <CardContent className="pt-8 flex flex-col h-full">
                      <Quote className="w-8 h-8 text-accent/30 mb-4 flex-shrink-0" />
                      <div className="flex gap-0.5 mb-4">
                        {Array.from({ length: testimonial.stars }).map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-gaius-gold text-gaius-gold" />
                        ))}
                      </div>
                      <blockquote className="text-lg text-muted-foreground mb-6 leading-relaxed flex-grow italic">
                        "{testimonial.quote}"
                      </blockquote>
                      <div className="border-t border-border pt-4 flex-shrink-0">
                        <div className="font-semibold text-foreground">{testimonial.author}</div>
                        <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>

          <Button
            variant="outline"
            size="icon"
            className="absolute -left-4 top-1/2 -translate-y-1/2 rounded-full h-10 w-10 bg-background/80 backdrop-blur-sm shadow-lg border-primary/20 hidden md:flex hover:bg-primary hover:text-primary-foreground transition-colors"
            onClick={prev}
            disabled={current === 0}
            aria-label={language === 'fr' ? 'Témoignage précédent' : 'Previous testimonial'}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="absolute -right-4 top-1/2 -translate-y-1/2 rounded-full h-10 w-10 bg-background/80 backdrop-blur-sm shadow-lg border-primary/20 hidden md:flex hover:bg-primary hover:text-primary-foreground transition-colors"
            onClick={next}
            disabled={current >= maxIndex}
            aria-label={language === 'fr' ? 'Témoignage suivant' : 'Next testimonial'}
          >
            <ChevronRight className="h-5 w-5" />
          </Button>

          <div className="flex justify-center gap-2 mt-8" role="tablist" aria-label={language === 'fr' ? 'Navigation témoignages' : 'Testimonials navigation'}>
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                role="tab"
                aria-selected={i === current}
                className={`w-3 h-3 rounded-full transition-all min-w-[44px] min-h-[44px] flex items-center justify-center p-0 before:block before:rounded-full before:transition-all ${
                  i === current ? 'before:w-6 before:h-3 before:bg-primary before:rounded-full' : 'before:w-3 before:h-3 before:bg-primary/25'
                }`}
                aria-label={language === 'fr' ? `Aller au groupe ${i + 1}` : `Go to group ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
